import { Button } from "@/components/flexkit/Button";
import { Switch } from "@/components/Switch";
import { Textarea } from "@/components/podkit/forms/Textarea";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { Text } from "@/components/podkit/typography/Text";
import { useGetOrganizationPolicies, useUpdateOrganizationPolicies } from "@/queries/organization-queries";
import { formatError } from "@/utils/errors";
import type { PartialMessage } from "@bufbuild/protobuf";
import type { OrganizationPolicies } from "gitpod-next-api/gitpod/v1/organization_pb";
import { useCallback, useEffect, useState, type FC } from "react";
import { ExternalLink } from "@/components/podkit/typography/ExternalLink";
import { PreviewInEnvironmentButton } from "@/routes/settings/organization/policies/PreviewInEnvironmentButton";

const MAX_EXECUTABLES = 20;

type ExecutableDenyListProps = {
    editable?: boolean;
};

export const ExecutableDenyList: FC<ExecutableDenyListProps> = ({ editable = true }) => {
    const { data: organizationPolicies } = useGetOrganizationPolicies();
    const updateOrganizationPolicies = useUpdateOrganizationPolicies();
    const { toast } = useToast();

    const [enabled, setEnabled] = useState(false);
    const [executables, setExecutables] = useState("");
    const [isDirty, setIsDirty] = useState(false);

    useEffect(() => {
        if (organizationPolicies?.executableDenyList) {
            setEnabled(organizationPolicies.executableDenyList.enabled);
            setExecutables(organizationPolicies.executableDenyList.executables.join("\n"));
        }
    }, [organizationPolicies]);

    const parseExecutables = useCallback(
        (text?: string): string[] =>
            (text ?? executables)
                .split("\n")
                .map((line) => line.trim())
                .filter(Boolean),
        [executables],
    );

    const buildParams = useCallback(
        (overrides: { enabled?: boolean; executables?: string }): PartialMessage<OrganizationPolicies> => ({
            executableDenyList: {
                enabled: overrides.enabled ?? enabled,
                executables: parseExecutables(overrides.executables),
            },
        }),
        [enabled, parseExecutables],
    );

    const handleToggle = useCallback(
        async (checked: boolean) => {
            if (checked) {
                const lines = parseExecutables();
                if (lines.length > MAX_EXECUTABLES) {
                    toast({
                        title: `Maximum ${MAX_EXECUTABLES} executables allowed`,
                        description: `You have ${lines.length} entries. Please reduce the list before enabling.`,
                    });
                    return;
                }
            }

            const prevState = enabled;
            setEnabled(checked);

            try {
                await updateOrganizationPolicies.mutateAsync(buildParams({ enabled: checked }));
                toast({
                    title: checked ? "Executable deny list enabled" : "Executable deny list disabled",
                });
            } catch (error) {
                console.error(error);
                setEnabled(prevState);
                toast({
                    title: "We couldn't save your changes. Sorry about that! Please retry.",
                    description: formatError(error),
                });
            }
        },
        [enabled, parseExecutables, buildParams, updateOrganizationPolicies, toast],
    );

    const handleExecutablesChange = useCallback((value: string) => {
        setExecutables(value);
        setIsDirty(true);
    }, []);

    const handleSave = useCallback(async () => {
        try {
            await updateOrganizationPolicies.mutateAsync(buildParams({}));
            setIsDirty(false);
            toast({
                title: "Executable deny list updated",
            });
        } catch (error) {
            console.error(error);
            toast({
                title: "We couldn't save your changes. Sorry about that! Please retry.",
                description: formatError(error),
            });
        }
    }, [buildParams, updateOrganizationPolicies, toast]);

    return (
        <div className="rounded-xl border border-border-base" data-testid="executable-deny-list-section">
            <div className="flex items-center gap-4 px-5 py-4">
                <Switch
                    id="executable-deny-list-toggle"
                    state={enabled ? "checked" : "unchecked"}
                    onToggle={handleToggle}
                    disabled={!editable}
                    aria-label="Enable executable deny list"
                    data-tracking-id="toggle-executable-deny-list"
                />
                <div className="flex flex-col gap-1">
                    <Text className="text-base font-semibold">Executable Deny List</Text>
                    <Text className="text-sm text-content-secondary">
                        Block specific executables from running in all environments{" "}
                        <ExternalLink
                            href="https://ona.com/docs/ona/organizations/policies/executable-deny-list"
                            iconSize="sm"
                        >
                            Learn more
                        </ExternalLink>
                    </Text>
                </div>
            </div>
            <div className="flex flex-col gap-4 border-t border-border-base px-5 py-4">
                <div className="flex flex-col gap-2">
                    <Text className="text-sm text-content-secondary">
                        Enter executable paths or names to block (one per line, max {MAX_EXECUTABLES})
                    </Text>
                    <Textarea
                        value={executables}
                        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                            handleExecutablesChange(e.target.value)
                        }
                        placeholder={"/usr/bin/curl\n/usr/bin/wget\nnc\nncat"}
                        minHeight={120}
                        maxHeight={400}
                        disabled={!editable}
                        className="resize-none font-mono text-sm"
                        data-testid="executable-deny-list-textarea"
                    />
                </div>
                <div className="flex items-start gap-3">
                    <Button
                        onClick={handleSave}
                        variant="secondary"
                        disabled={!isDirty || !editable}
                        loading={updateOrganizationPolicies.isPending}
                        data-testid="save-executable-deny-list-button"
                        data-tracking-id="save-executable-deny-list"
                    >
                        Save Changes
                    </Button>
                    <div className="ml-auto">
                        <PreviewInEnvironmentButton
                            executables={parseExecutables()}
                            disabled={!editable}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};
